import React, { useState, useEffect } from 'react';
import { Spinner, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import { projectsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import SiteDetailModal from './SiteDetailModal';

interface Site {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  project: {
    id: string;
    name: string;
    code: string;
    city: { id: string; name: string; parentId?: string | null };
  };
  city?: { id: string; name: string; parentId?: string | null } | null;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { id: string; name: string; parentId?: string | null };
}

interface City {
  id: string;
  name: string;
  code?: string;
}

interface District {
  id: string;
  name: string;
}

interface SitesListModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
};

const SitesListModal: React.FC<SitesListModalProps> = ({
  isOpen,
  toggle,
  project,
  onUpdate,
}) => {
  const isAdmin = useIsAdmin();
  const [isExpanded, setIsExpanded] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);

  // Site detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // New site modal state
  const [newSiteModalOpen, setNewSiteModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    status: 'ACTIVE',
    provinceId: '',
    districtId: '',
  });

  useEffect(() => {
    if (isOpen && project) {
      fetchSites();
    }
  }, [isOpen, project]);

  // İlleri yükle
  const loadCities = async () => {
    try {
      const res = await projectsAPI.getCities();
      setCities(res.data);
    } catch (error) {
      console.error('İller yüklenemedi:', error);
    }
  };

  // İlçeleri yükle
  const loadDistricts = async (provinceId: string) => {
    if (!provinceId) {
      setDistricts([]);
      return;
    }
    try {
      setLoadingDistricts(true);
      const res = await projectsAPI.getDistricts(provinceId);
      setDistricts(res.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const handleProvinceChange = (provinceId: string) => {
    setFormData(prev => ({ ...prev, provinceId, districtId: '' }));
    setDistricts([]);
    if (provinceId) {
      loadDistricts(provinceId);
    }
  };

  const fetchSites = async () => {
    if (!project) return;

    try {
      setLoading(true);
      const response = await projectsAPI.getProjectSites(project.id);
      setSites(response.data);
    } catch (error) {
      console.error('Fetch sites error:', error);
      toast.error('Şantiyeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const openSiteDetail = (site: Site) => {
    setSelectedSite(site);
    setDetailModalOpen(true);
  };

  const closeSiteDetail = () => {
    setDetailModalOpen(false);
    setSelectedSite(null);
  };

  const handleSiteUpdate = () => {
    fetchSites();
    onUpdate?.();
  };

  const toggleNewSiteModal = () => {
    if (!newSiteModalOpen) {
      // Modal açılıyor - illeri yükle, projenin ilini varsayılan yap
      loadCities();
      const defaultProvinceId = project?.city.parentId || project?.city.id || '';
      setFormData(prev => ({ ...prev, provinceId: defaultProvinceId }));
      if (defaultProvinceId) {
        loadDistricts(defaultProvinceId);
      }
    } else {
      // Modal kapanıyor - formu temizle
      setFormData({ name: '', code: '', address: '', status: 'ACTIVE', provinceId: '', districtId: '' });
      setDistricts([]);
    }
    setNewSiteModalOpen(!newSiteModalOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;

    if (!formData.name || !formData.code) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      await projectsAPI.createSite({
        name: formData.name,
        code: formData.code,
        projectId: project.id,
        address: formData.address || undefined,
        cityId: formData.districtId || formData.provinceId || undefined,
      });
      toast.success('Şantiye oluşturuldu');
      toggleNewSiteModal();
      fetchSites();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  if (!project) return null;

  const headerActions = (
    <>
      {isAdmin && (
        <button
          type="button"
          className="side-modal-expand-btn me-1"
          onClick={toggleNewSiteModal}
          title="Yeni Şantiye"
          style={{ color: '#0ab39c' }}
        >
          <i className="ri-add-line"></i>
        </button>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <>
      <SideModal
        isOpen={isOpen}
        toggle={toggle}
        title="Şantiyeler"
        size={isExpanded ? 'expanded' : 'lg'}
        headerActions={headerActions}
        hideBackdrop
      >
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
          {/* Header */}
          <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
            <h4 className="mb-1" style={{ fontWeight: 600 }}>
              {project.name}
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
              {project.city.name} • {project.code}
            </p>
          </div>

          {/* Sites List */}
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : sites.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-3">Bu projede henüz şantiye bulunmuyor.</p>
              {isAdmin && (
                <Button color="primary" size="sm" onClick={toggleNewSiteModal}>
                  <i className="ri-add-line me-1"></i>
                  İlk Şantiyeyi Ekle
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Şantiye Listesi
                </span>
                <span className="text-muted small">{sites.length} adet</span>
              </div>
              <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                {sites.map((site, idx) => (
                  <div
                    key={site.id}
                    className="d-flex align-items-center justify-content-between px-3 py-3"
                    style={{
                      borderBottom: idx < sites.length - 1 ? '1px solid #e9ecef' : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => openSiteDetail(site)}
                  >
                    <div style={{ flex: 1 }}>
                      <div className="d-flex align-items-center mb-1">
                        <span style={{ fontWeight: 500 }}>{site.name}</span>
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                          ({site.code})
                        </span>
                      </div>
                      {site.address && (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                          {site.address}
                        </p>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <span
                        className={`px-2 py-1 rounded-pill small me-2 ${statusStyles[site.status]}`}
                        style={{ fontWeight: 500 }}
                      >
                        {statusLabels[site.status]}
                      </span>
                      <i className="ri-arrow-right-s-line text-muted"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SideModal>

      {/* Site Detail Modal */}
      <SiteDetailModal
        isOpen={detailModalOpen}
        toggle={closeSiteDetail}
        site={selectedSite}
        onUpdate={handleSiteUpdate}
      />

      {/* New Site Modal */}
      <AdaptiveModal
        isOpen={newSiteModalOpen}
        toggle={toggleNewSiteModal}
        title="Yeni Şantiye"
        size="md"
        footer={
          <>
            <Button color="secondary" onClick={toggleNewSiteModal}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="site-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="site-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Şantiye Kodu *</Label>
            <Input
              type="text"
              value={formData.code}
              onChange={(e) =>
                setFormData({ ...formData, code: e.target.value.toUpperCase() })
              }
              placeholder="Örn: S-001"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Şantiye Adı *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Şantiye adını girin"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>İl</Label>
            <Input
              type="select"
              value={formData.provinceId}
              onChange={(e) => handleProvinceChange(e.target.value)}
            >
              <option value="">İl Seçin</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.code ? `${city.code} - ${city.name}` : city.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>İlçe</Label>
            <Input
              type="select"
              value={formData.districtId}
              onChange={(e) => setFormData({ ...formData, districtId: e.target.value })}
              disabled={!formData.provinceId || loadingDistricts}
            >
              <option value="">
                {loadingDistricts ? 'Yükleniyor...' : 'İlçe Seçin (Opsiyonel)'}
              </option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Adres</Label>
            <Input
              type="textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Şantiye adresi (opsiyonel)"
              rows={2}
            />
          </FormGroup>
          <FormGroup>
            <Label>Durum</Label>
            <Input
              type="select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="ACTIVE">Aktif</option>
              <option value="SUSPENDED">Askıda</option>
            </Input>
          </FormGroup>
        </Form>
      </AdaptiveModal>
    </>
  );
};

export default SitesListModal;
